import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Map as MapIcon } from "lucide-react";
import {
  BASEMAPS,
  getActiveBasemapId,
  setActiveBasemap,
} from "../../../config/basemaps";
import { SectionCard } from "../../../components/ui/SectionCard";

// ─────────────────────────────────────────────────────────────────────────────
// MapStylePreferences — preferencia local del usuario para el estilo del basemap
// usado en TODOS los mapas de la aplicación. Persiste en localStorage y se
// propaga en caliente vía el evento 'basemapchange'.
// ─────────────────────────────────────────────────────────────────────────────

// Mini swatch: muestra una muestra del tile claro y otra del oscuro tomando
// coordenadas reales (Santiago z=11) para que la diferencia se aprecie.
function Swatch({ url, subdomains }) {
  const z = 11;
  const x = 622;
  const y = 1225;
  const sub = subdomains ? subdomains[0] : "a";
  const src = url
    .replace("{s}", sub)
    .replace("{z}", z)
    .replace("{x}", x)
    .replace("{y}", y)
    .replace("{r}", "");
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      loading="lazy"
      className="as-basemap-swatch w-full h-32 object-cover"
      onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
    />
  );
}

export default function MapStylePreferences() {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState(getActiveBasemapId());

  const handleSelect = (id) => {
    setActiveBasemap(id);
    setActiveId(id);
  };

  const entries = Object.entries(BASEMAPS);

  return (
    <SectionCard
      icon={<MapIcon className="w-4 h-4" aria-hidden="true" />}
      title={t("Estilo de mapa")}
      subtitle={t("Elige el estilo del mapa base usado en seguimiento, rutas y flota. El cambio se aplica de inmediato.")}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {entries.map(([id, basemap]) => {
          const isActive = id === activeId;
          return (
            <button
              key={id}
              type="button"
              aria-pressed={isActive}
              onClick={() => handleSelect(id)}
              className={
                "relative text-left rounded-xl border overflow-hidden transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 " +
                (isActive
                  ? "border-brand-500 ring-1 ring-brand-500"
                  : "border-border hover:border-brand-300")
              }
            >
              {isActive && (
                <span className="absolute top-2 right-2 z-10 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-600 text-white shadow">
                  <Check size={14} aria-hidden="true" />
                </span>
              )}

              <div className="grid grid-cols-2">
                <div className="relative">
                  <Swatch url={basemap.light.url} subdomains={basemap.light.subdomains} />
                  <span className="absolute bottom-0 left-0 px-1.5 py-0.5 text-[10px] font-medium bg-black/40 text-white rounded-tr">
                    {t("Claro")}
                  </span>
                </div>
                <div className="relative">
                  <Swatch url={basemap.dark.url} subdomains={basemap.dark.subdomains} />
                  <span className="absolute bottom-0 left-0 px-1.5 py-0.5 text-[10px] font-medium bg-black/40 text-white rounded-tr">
                    {t("Oscuro")}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 px-3 py-2.5 bg-bg-muted border-t border-border">
                <MapIcon
                  size={16}
                  className={isActive ? "text-brand-600" : "text-fg-subtle"}
                  aria-hidden="true"
                />
                <span className={"text-sm font-medium " + (isActive ? "text-brand-700" : "text-fg")}>
                  {basemap.label}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </SectionCard>
  );
}
